import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewChildren,
  forwardRef,
  signal,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { BaseModalComponent } from 'showtix-shared';
import { ButtonComponent } from 'ui-elements/button';
import { FormFieldModule } from 'ui-elements/form-field';
import { BaseInputDirective } from 'ui-elements/form/input/base-input.directive';
import { ModalComponent } from 'ui-elements/modal';
import { FormFieldComponent } from 'ui-elements/form-field';
import { CheckboxProjectionComponent } from 'ui-elements/form/checkbox-projection';
import {
  FormArray,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { BehaviorSubject, Subject, map, takeUntil } from 'rxjs';
import {
  CountryService,
  EmailInterestResponse,
  MarketingService,
  StayInTouchRequest,
} from 'api';
import {
  ForbiddenDomainsValidatorDirective,
  CommonModule as SSCommonModule,
  regularExpressions,
  updateAllValueAndValidity,
} from 'common';
import { BreakpointObserver } from '@angular/cdk/layout';
import { FormErrorListingComponent } from '../../../../shared/components/form-error-listing/form-error-listing.component';
import { SelectData, ssSelectImports } from 'ui-elements/select';
import { NgxMaskDirective } from 'ngx-mask';

@Component({
  selector: 'ss-www-stay-in-touch-form',
  imports: [
    CommonModule,
    ModalComponent,
    FormFieldModule,
    FormsModule,
    ReactiveFormsModule,
    BaseInputDirective,
    NgxMaskDirective,
    ssSelectImports,
    ButtonComponent,
    SSCommonModule,
    CheckboxProjectionComponent,
    FormErrorListingComponent,
    ForbiddenDomainsValidatorDirective,
  ],
  templateUrl: './stay-in-touch-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StayInTouchFormComponent
  extends BaseModalComponent<Object>
  implements OnInit, OnDestroy
{
  private countryService = inject(CountryService);
  private breakpointObserver = inject(BreakpointObserver);
  private marketingService = inject(MarketingService);
  private cdr = inject(ChangeDetectorRef);

  @Input() modalWidth = 50;

  @Input() set emailInitialValue(initialValue: string) {
    this.form.controls.customer.controls.contact.controls.emailAddress.setValue(
      initialValue
    );
  }

  @Output() formSubmit = new EventEmitter<StayInTouchRequest>();
  isSmallScreen$ = this.breakpointObserver
    .observe(['(max-width: 768px)'])
    .pipe(map((res) => res.breakpoints['(max-width: 768px)']));
  countries$ = new BehaviorSubject<SelectData[] | null>(null);
  formErrors = signal<string[] | null>(null);
  @ViewChildren(forwardRef(() => FormFieldComponent))
  formFields?: FormFieldComponent[];
  @ViewChild('errorListing') errorListing?: ElementRef<HTMLDivElement>;
  form = new FormGroup({
    customer: new FormGroup({
      contact: new FormGroup({
        firstName: new FormControl('', [
          Validators.required,
          Validators.maxLength(20),
        ]),
        lastName: new FormControl('', [
          Validators.required,
          Validators.maxLength(20),
        ]),
        phoneNumber: new FormControl('', [
          Validators.required,
          Validators.pattern(regularExpressions.onlyNumbersPattern),
          Validators.maxLength(20),
          Validators.minLength(10),
        ]),
        emailAddress: new FormControl('', [
          Validators.required,
          Validators.maxLength(50),
        ]),
      }),
      address: new FormGroup({
        country: new FormControl('USA', [Validators.required]),
        addressLine1: new FormControl('', [Validators.required]),
        addressLine2: new FormControl(''),
        city: new FormControl('', [Validators.required]),
        state: new FormControl('', [
          Validators.required,
          Validators.maxLength(2),
        ]),
        zipcode: new FormControl('', [Validators.required]),
      }),
    }),
    marketingInterestIds: new FormArray<FormControl<boolean>>([]),
  });
  interests$ = new BehaviorSubject<EmailInterestResponse[] | null>(null);

  onDestroy$ = new Subject<void>();

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
  ngOnInit(): void {
    this.countryService
      .getCountries()
      ?.pipe(takeUntil(this.onDestroy$))
      .subscribe((countries) => {
        this.countries$.next(
          countries.map((key) => ({
            value: key.code,
            name: key.name.toLocaleUpperCase(),
          }))
        );
      });

    this.marketingService
      .getInterests()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((interests) => {
        interests.forEach(() => {
          this.form.controls.marketingInterestIds.push(
            new FormControl(false, { nonNullable: true })
          );
        });
        this.interests$.next(interests);
        this.cdr.markForCheck();
      });

    this.form.controls.customer.controls.address.controls.country.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((value) => {
        this.form.controls.customer.controls.address.controls.state.setValue(
          ''
        );
        if (value === 'USA') {
          this.form.controls.customer.controls.address.controls.state.setValidators(
            [Validators.required, Validators.maxLength(2)]
          );
        } else {
          this.form.controls.customer.controls.address.controls.state.setValidators(
            [Validators.required]
          );
        }
      });
  }

  onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      updateAllValueAndValidity(Object.values(this.form.controls));
      this.formErrors.set(
        this.formFields?.map((field) => field.allErrorText).flat() ?? []
      );
      this.errorListing?.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
      return;
    }

    const request = {
      ...this.form.value,
      marketingInterestIds: this.interests$.value?.reduce(
        (acc, interest, index) => {
          if (this.form.controls.marketingInterestIds.controls[index].value) {
            acc.push(interest.interestId);
          }
          return acc;
        },
        [] as string[]
      ),
    } as StayInTouchRequest;
    this.formSubmit.emit(request);
  }
}
